import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiClock, FiRefreshCw, FiMessageSquare,
  FiThumbsUp, FiThumbsDown, FiX,
} from 'react-icons/fi';
import { useToast } from '../../context/ToastContext';
import PreviewIcon from '../../assets/images/ViewDetailsIcon.svg';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import '../../styles/ValChoiceClassificationHistory.css';
const PAGE_SIZE = 10;
const getToken  = () => localStorage.getItem('toolboxToken') || '';

const formatDate = (d: string): string => {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${m[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
  } catch { return d; }
};

interface ValChoiceHistoryRow {
  id: string;
  transactionId: string;
  insuredName: string;
  carrierName: string;
  created: string;
  userName: string;
  fileName: string;
  isLiked: boolean | null;
  totalCount: number;
}

const ValChoiceClassificationHistory: React.FC = () => {
  const [rows, setRows]             = useState<ValChoiceHistoryRow[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  /* Preview modal */
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  /* Feedback */
  const [feedbackRowId,      setFeedbackRowId]      = useState<string | null>(null);
  const [feedbackText,       setFeedbackText]        = useState('');
  const [feedbackRating,     setFeedbackRating]      = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]      = useState<{ top: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting]  = useState(false);
  const [feedbackError,      setFeedbackError]       = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  const { showSuccess, showError } = useToast();

  /* ── Fetch ── */
  const fetchData = useCallback(async (pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/valchoice/valchoiceTransactions?pageNumber=${pageNum}&recordCount=${PAGE_SIZE}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data: ValChoiceHistoryRow[] = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      setTotalCount(data.length > 0 ? (data[0].totalCount ?? data.length) : 0);
    } catch (e: any) {
      setError(e.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(page); }, [fetchData, page]);
  useEffect(() => { setPage(1); }, [search]);

  /* ── Close feedback on outside click ── */
  useEffect(() => {
    if (!feedbackRowId) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackRowId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.insuredName?.toLowerCase().includes(q) ||
      r.carrierName?.toLowerCase().includes(q) ||
      r.userName?.toLowerCase().includes(q) ||
      r.fileName?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  /* ── Sorting ── */
  type SortKey = 'insuredName' | 'carrierName' | 'created' | 'userName' | 'fileName';
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortKey === 'created') {
        const av = a.created ? new Date(a.created).getTime() : 0;
        const bv = b.created ? new Date(b.created).getTime() : 0;
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const av = (a[sortKey] || '').toString().toLowerCase();
      const bv = (b[sortKey] || '').toString().toLowerCase();
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const renderSortableTh = (label: string, key: SortKey) => (
    <th
      className="qp-table__th valchoice-history__th-sortable"
      onClick={() => onSort(key)}
      title={`Sort by ${label}`}
    >
      {label}
      <span className="valchoice-history__sort-icon" style={{ opacity: sortKey === key ? 1 : 0.35 }}>
        {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
      </span>
    </th>
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageList = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
    .reduce<(number | '…')[]>((acc, p, idx, arr) => {
      if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
      acc.push(p);
      return acc;
    }, []);

  /* ── Feedback ── */
  const openFeedback = useCallback((row: ValChoiceHistoryRow, e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackRowId(row.id);
    setFeedbackText('');
    setFeedbackRating(row.isLiked ?? null);
    setFeedbackError('');
    setFeedbackAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
  }, []);

  const closeFeedback = useCallback(() => {
    setFeedbackRowId(null);
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedbackError('');
  }, []);

  const saveRating = useCallback((id: string, isLiked: boolean | null) => {
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/valchoice/valchoiceratings`,
      { Id: id, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Rating submitted!')).catch(() => { /* silent */ });
  }, []);

  const onThumbsUp = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.id === feedbackRowId);
    const next: boolean | null = cur?.isLiked === true ? null : true;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.id === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onThumbsDown = useCallback(() => {
    if (!feedbackRowId) return;
    const cur = rows.find(r => r.id === feedbackRowId);
    const next: boolean | null = cur?.isLiked === false ? null : false;
    setFeedbackRating(next);
    setRows(prev => prev.map(r => r.id === feedbackRowId ? { ...r, isLiked: next } : r));
    saveRating(feedbackRowId, next);
    closeFeedback();
  }, [feedbackRowId, rows, saveRating, closeFeedback]);

  const onSubmitFeedback = useCallback(() => {
    if (!feedbackRowId) return;
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${(process.env.REACT_APP_DataFabric_Url || '')}/api/valchoice/valchoicefeedback`,
      { Id: feedbackRowId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); showError('Unknown error while submitting feedback.'); });
  }, [feedbackRowId, feedbackText, closeFeedback]);

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div
        className="qp-history"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div className="qp-history__header">
          <h3 className="qp-history__title valchoice-history__title-reset">Carrier Rating History</h3>
          <div className="valchoice-history__header-actions">
            <div className="cs-client-search__input-wrap valchoice-history__search-wrap">
              <FiSearch size={13} className="cs-client-search__icon" />
              <input
                className="cs-client-search__input"
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <motion.button
              className="csh-refresh-btn"
              onClick={() => fetchData(page)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FiRefreshCw size={13} className={loading && rows.length > 0 ? 'csh-spin' : ''} />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Body */}
        <div className="qp-history__body">
          {error ? (
            <div className="qp-empty-state valchoice-history__error-state">
              <FiClock size={28} /><p>{error}</p>
            </div>
          ) : loading ? (
            <div className="qp-history-loader valchoice-history__loader">
              <div className="qp-history-loader__orb">
                <div className="qp-history-loader__ring qp-history-loader__ring--1" />
                <div className="qp-history-loader__ring qp-history-loader__ring--2" />
                <div className="qp-history-loader__core"><FiClock size={16} /></div>
              </div>
              <div className="qp-history-loader__text">
                <p className="qp-history-loader__title">Loading History</p>
                <p className="qp-history-loader__sub">
                  Fetching carrier rating records
                  <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
                </p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="qp-empty-state">
              <FiClock size={28} />
              <p>{search ? 'No records match your search.' : 'No carrier rating reports yet.'}</p>
            </div>
          ) : (
            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    {renderSortableTh('Insured Name', 'insuredName')}
                    {renderSortableTh('Carrier Name', 'carrierName')}
                    {renderSortableTh('Submitted Date', 'created')}
                    {renderSortableTh('User', 'userName')}
                    {renderSortableTh('File (URL is valid for 1 year)', 'fileName')}
                    <th className="qp-table__th valchoice-history__cell-center">Action</th>
                    <th className="qp-table__th valchoice-history__cell-center">Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <tr key={row.id ?? i} className="qp-table__row">
                      <td className="qp-table__td">{row.insuredName || '—'}</td>
                      <td className="qp-table__td">{row.carrierName || '—'}</td>
                      <td className="qp-table__td valchoice-history__td-date">
                        {formatDate(row.created)}
                      </td>
                      <td className="qp-table__td">{row.userName || '—'}</td>
                      <td className="qp-table__td valchoice-history__td-filename">
                        {row.fileName ? (
                          <span title={row.fileName} className="valchoice-history__filename-text">
                            {row.fileName}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="qp-table__td valchoice-history__cell-center">
                        {row.fileName && (
                          <div className="qp-table__actions">
                            <button
                              className="qp-table__action-btn"
                              title="Preview report"
                              onClick={() => setPreviewUrl(row.fileName)}
                            >
                              <img src={PreviewIcon} alt="Preview" width={30} height={30} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="qp-table__td valchoice-history__cell-center">
                        <button
                          className="qp-table__action-btn valchoice-history__action-btn-center"
                          title="Feedback"
                          onClick={e => openFeedback(row, e)}
                        >
                          <FiMessageSquare size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="qp-pagination">
              <span className="qp-pagination__info">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="qp-pagination__controls">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(1)} title="First">«</button>
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} title="Previous">‹</button>
                {pageList.map((p, i) =>
                  p === '…'
                    ? <span key={`e-${i}`} className="qp-pagination__ellipsis">…</span>
                    : <button
                        key={p}
                        className={`qp-pagination__btn${page === p ? ' qp-pagination__btn--active' : ''}`}
                        onClick={() => setPage(p as number)}
                      >{p}</button>
                )}
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} title="Next">›</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)} title="Last">»</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Feedback popup ── */}
      {feedbackRowId && feedbackAnchor && ReactDOM.createPortal(
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{ top: feedbackAnchor.top, right: feedbackAnchor.right }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUp}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDown}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedbackError && (
            <p className="csfb-error">{feedbackError}</p>
          )}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedback}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* ── PDF Preview modal ── */}
      {previewUrl && ReactDOM.createPortal(
        <div
          className="valchoice-history__preview-overlay"
          onClick={() => setPreviewUrl(null)}
        >
          <div
            className="valchoice-history__preview-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="valchoice-history__preview-header">
              <h3 className="valchoice-history__preview-title">
                Carrier Rating Report
              </h3>
              <button
                onClick={() => setPreviewUrl(null)}
                className="valchoice-history__preview-close-btn"
              >
                <FiX size={18} />
              </button>
            </div>
            <iframe
              src={previewUrl}
              className="valchoice-history__preview-iframe"
              title="Carrier Rating Report Preview"
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ValChoiceClassificationHistory;

